import React, { useEffect, useState } from 'react';
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import { MdClose, MdShoppingCart } from "react-icons/md";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from 'react-router-dom';
import { BsArrowRight } from "react-icons/bs";
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, removeFromCart, updateCartPrices } from '../../features/cart/cartSlice';
import { IMAGE_PATH } from '../../utils/constants';
import apiClient from '../../services/apiClient';

const Cart = () => {
  const cartItems = useSelector(state => state.cart.items);
  const dispatch = useDispatch();
  const [total, setTotal] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [isCheckingPrices, setIsCheckingPrices] = useState(false);
  const [priceChanges, setPriceChanges] = useState([]);
  
  const breadcrumbItems = [
    { label: "Home", url: "/" },
    { label: "Cart" },
  ];
  
  const user = useSelector(state => state.auth.user);
  const navigate = useNavigate();

  const updateQuantity = (product, quantityChange) => {
    const newQuantity = quantityChange;
    dispatch(addToCart({
      ...product,
      quantity: newQuantity
    }));
  };

  // Price checker function
  const checkProductPrices = async () => {
    if (cartItems.length === 0) return;

    setIsCheckingPrices(true);
    try {
      const productIds = cartItems.map(item => ({
        id: item.id,
        is_combo: item.is_combo || false
      }));

      const response = await apiClient.post('/products/check-prices', {
        items: productIds,
        user_id: user?.id || null
      });

      if (response.data.status) {
        const updatedPrices = response.data.data;
        const changes = [];

        // Compare current prices with updated prices
        cartItems.forEach(item => {
          const updatedProduct = updatedPrices.find(p => 
            p.id === item.id && p.is_combo === (item.is_combo || false)
          );
          
          if (updatedProduct) {
            const currentSalePrice = item.sale_price || item.price;
            const updatedSalePrice = updatedProduct.sale_price || updatedProduct.price;
            
            if (updatedSalePrice !== currentSalePrice) {
              changes.push({
                id: item.id,
                name: item.name,
                oldSalePrice: currentSalePrice,
                newSalePrice: updatedSalePrice,
                oldPrice: item.price,
                newPrice: updatedProduct.price,
                isPriceIncreased: updatedSalePrice > currentSalePrice
              });
            }
          }
        });

        if (changes.length > 0) {
          setPriceChanges(changes);
          showPriceChangeNotification(changes);
          
          // Update cart with new prices while preserving quantities
          const updatedCartItems = cartItems.map(item => {
            const updatedProduct = updatedPrices.find(p => 
              p.id === item.id && p.is_combo === (item.is_combo || false)
            );
            
            if (updatedProduct) {
              return {
                ...item,
                price: updatedProduct.price,
                sale_price: updatedProduct.sale_price,
                original_price: updatedProduct.original_price,
                original_sale_price: updatedProduct.original_sale_price,
                is_offer: updatedProduct.is_offer,
                discount_amount: updatedProduct.discount_amount,
                discount_percentage: updatedProduct.discount_percentage,
                applied_offer: updatedProduct.applied_offer
              };
            }
            return item;
          });
          
          dispatch(updateCartPrices(updatedCartItems));
        } else {
          setPriceChanges([]);
          toast.success('All prices are up to date!');
        }
      } else {
        toast.error('Failed to check prices: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error checking product prices:', error);
      toast.error('Failed to verify product prices');
    } finally {
      setIsCheckingPrices(false);
    }
  };

  const showPriceChangeNotification = (changes) => {
    const priceIncreased = changes.filter(change => change.isPriceIncreased);
    const priceDecreased = changes.filter(change => !change.isPriceIncreased);

    if (priceIncreased.length > 0) {
      toast.error(
        `${priceIncreased.length} item(s) price increased. Please review your cart.`,
        { duration: 6000 }
      );
    }
    
    if (priceDecreased.length > 0) {
      toast.success(
        `${priceDecreased.length} item(s) price decreased! You saved money.`,
        { duration: 5000 }
      );
    }
  };

  // Calculate totals whenever cart items change
  useEffect(() => {
    calculateTotals();
  }, [cartItems]);

  const calculateTotals = () => {
    let totalAmount = 0;
    let subtotalAmount = 0;
    
    cartItems.forEach((item) => {
      const itemPrice = item.sale_price || item.price;
      totalAmount += itemPrice * item.quantity;
      subtotalAmount += (item.original_price || item.price) * item.quantity;
    });
    
    setTotal(totalAmount);
    setSubtotal(subtotalAmount);
    setDiscount(subtotalAmount - totalAmount);
  };

  // Auto-check prices when cart loads or items change
  // useEffect(() => {
  //   if (cartItems.length > 0) {
  //     checkProductPrices();
  //   }
  // }, []);

  const removeItem = (id) => {
    const itemToRemove = cartItems.find(item => item.id === id);
    if (itemToRemove) {
      dispatch(removeFromCart(id));
      toast.success(`${itemToRemove.name} removed from cart!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleCheckout = async () => {
    try {
      // Verify prices before checkout
      setIsCheckingPrices(true);
      await checkProductPrices();

      localStorage.removeItem("redirecturl");
      if (!user) {
        localStorage.setItem('redirecturl', '/cart/cart');
        navigate('/auth/login');
        return;
      }
        
      const response = await apiClient.post('/checkout-check', {
        items: cartItems,
      });
      
      const data = response.data;
      if (data.status) {
        navigate('/products/checkout');
      } else {
        toast.error("Out Of Stock: " + data.message);
      }
    } catch (err) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate('/auth/login');
      } else {
        toast.error('Failed to process checkout');
      }
    } finally {
      setIsCheckingPrices(false);
    }
  };

  

  return (
    <div id='Cart'>
      <div className="BreadCrumbsBox">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      <div className='container'>
        {cartItems.length > 0 ? (
          <div className='CartMain'>
            <div className='CartLeft'>
              
              
              <form>
                <table className="CartTable">
                  <thead>
                    <tr>
                      <th className="TableHead">Product</th>
                      <th className="TableHead">Price</th>
                      <th className="TableHead">Quantity</th>
                      <th className="TableHead">Total</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map((item) => {
                      const priceChange = priceChanges.find(change => change.id === item.id);
                      const currentSalePrice = item.sale_price || item.price;
                      
                      return (
                        <tr className="ProductRow" key={`${item.id}-${item.is_combo ? 'combo' : 'product'}`}>
                          <td>
                            <div className="ProductImgTitleBox">
                              <a href={item.is_combo ? `/combo/${item.id}` : `/products/${item.id}`} className="ProductImg">
                                <img 
                                  src={item.is_combo 
                                    ? IMAGE_PATH + (item.image_path || item.media?.[0]?.file_path) 
                                    : IMAGE_PATH + (item.media?.[0]?.file_path || item.image_path)
                                  } 
                                  alt={item.name}
                                  onError={(e) => {
                                    e.target.src = '/images/placeholder-product.jpg';
                                  }}
                                  
                                />
                              </a>
                              <div className="ProductTitleSizeBox">
                                <p className="ProductTitle">
                                  <a href={item.is_combo ? `/combo/${item.id}` : `/products/${item.id}`}>
                                    {item.name}
                                  </a>
                                </p>
                                <div className="ProductSize">
                                  <p>{item.is_combo ? 'Combo Offer' : `Size: ${item.size || 'N/A'}`}</p>
                                </div>
                                {/* {item.is_offer && (
                                  <div className="OfferBadge">
                                    Offer: {item.discount_percentage}% off
                                  </div>
                                )} */}
                              </div>
                            </div>
                          </td>
                          <td className="Price">
                            {priceChange ? (
                              <div className="PriceComparison">
                                {/* <span className="OldPrice">₹{priceChange.oldSalePrice}</span> */}
                                <span className={`NewPrice ${priceChange.isPriceIncreased ? 'price-increase' : 'price-decrease'}`}>
                                  ₹{currentSalePrice}
                                </span>
                              </div>
                            ) : (
                              <div className="PriceDisplay">
                                {item.original_sale_price > currentSalePrice ? (
                                  <>
                                    <span className="OriginalPrice">₹{item.original_sale_price}</span>
                                    {/* <span className="SalePrice">₹{currentSalePrice}</span> */}
                                  </>
                                ) : (
                                  <span>₹{currentSalePrice}</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="QuantityBox">
                            <div className="QuantityBtn">
                              <button 
                                className="MinusBtn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  updateQuantity(item, -1);
                                }}
                                disabled={isCheckingPrices}
                              >
                                <p>-</p>
                              </button>
                              <input 
                                className="QuantityShow" 
                                type="text" 
                                value={item.quantity}
                                readOnly
                              />
                              <button 
                                className="PlusBtn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  updateQuantity(item, 1);
                                }}
                                disabled={isCheckingPrices}
                              >
                                <p>+</p>
                              </button>
                            </div>
                          </td>
                          <td className="TotalPrice">
                            ₹{(currentSalePrice * item.quantity).toFixed(2)}
                          </td>
                          <td className="CloseIcon">
                            <MdClose 
                              onClick={() => removeItem(item.id)}
                              className="CloseIconBtn"
                              disabled={isCheckingPrices}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </form>
    
            </div>
            
            <div className='CartRight'>
              <div className='CartSummaryBox'>
                <div className='CartSummaryTitle'>
                  <h2>Order Summary</h2>
                  {isCheckingPrices && (
                    <div className="PriceCheckingIndicator">
                      Verifying prices...
                    </div>
                  )}
                </div>
                
                <div className='TotalPriceBox'>
                  <div className='SubTotal'>
                    <p>Subtotal</p>
                    <p>₹{subtotal.toFixed(2)}</p>
                  </div>
                  <div className='Discount'>
                    <p>Discount</p>
                    <p>-₹{discount.toFixed(2)}</p>
                  </div>
                  <div className='Total'>
                    <p>Total</p>
                    <p>₹{total.toFixed(2)}</p>
                  </div>
                </div>
                
                <div className='CartBtn'>
                  <a onClick={handleCheckout} className='CheckoutBtn'>
                    <button disabled={isCheckingPrices || priceChanges.length > 0}>
                      {isCheckingPrices ? 'Checking...' : 'Proceed to Checkout'}
                    </button>
                    <BsArrowRight className="ArrowIcon" />
                  </a>
                  <Link to="/products/product" className='ContinueBtn'>
                    <button>Continue Shopping</button>
                    <BsArrowRight className="ArrowIcon" />
                  </Link>
                </div>
                
                
              </div>
            </div>
          </div>
        ) : (
          <div className='EmptyCart'>
            <div className='EmptyCartIcon'>
              <MdShoppingCart />
            </div>
            <h3>Your Cart is Empty</h3>
            <p>You've removed all items from your cart</p>
            <Link to="/products/product" className='ContinueShoppingBtn'>
              Continue Shopping
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;